import * as React from "react";
import { Menu, usePermissions, MenuItemLink, useSidebarState } from "react-admin";
import { Divider, Box, Badge } from "@mui/material";

import BusinessIcon from "@mui/icons-material/Business";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import InterpreterModeIcon from "@mui/icons-material/InterpreterMode";
import ConstructionIcon from "@mui/icons-material/Construction";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import MailIcon from "@mui/icons-material/Mail";
import CampaignIcon from "@mui/icons-material/Campaign";
import StarsIcon from "@mui/icons-material/Stars";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventNoteIcon from "@mui/icons-material/EventNote";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import GavelIcon from "@mui/icons-material/Gavel";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import WorkIcon from "@mui/icons-material/Work";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SettingsIcon from "@mui/icons-material/Settings";

import { UserAvatarIcon } from "./UserAvatarIcon";
import { can } from "../auth/can";
import { useInboxUnreadCount } from "../messages/useInboxUnreadCount";

function InboxMenuItem({ clubId }: { clubId?: number | null }) {
  const unread = useInboxUnreadCount(15000, clubId);

  return (
    <MenuItemLink
      to="/inbox"
      primaryText="Posteingang"
      leftIcon={
        <Badge badgeContent={unread} color="error" max={99}>
          <MailIcon />
        </Badge>
      }
    />
  );
}

export const AppMenu = () => {
  const { permissions, isPending } = usePermissions();
  const [open] = useSidebarState();

  // Kein flicker: solange permissions nicht da sind -> nichts rendern
  if (isPending) return null;
  const showClubManagement = Boolean(permissions?.resources);

  /**
   * ✅ Mobile Fixes:
   * - Der Container ist scrollable: overflowY:auto
   * - Unten extra Platz + Safe-Area (iOS): env(safe-area-inset-bottom)
   * - Touch-Scroll "smooth" auf iOS: WebkitOverflowScrolling:'touch'
   *
   * Wichtig: height: 100% funktioniert, weil der Sidebar/Drawer typischerweise
   * eine feste Höhe hat. Falls nicht, kann man auf 100dvh wechseln.
   */
  return (
    <Box
      sx={{
        overflowX: "hidden",
        overflowY: "visible",
        height: "auto",
        minHeight: "100%",
        pb: "calc(var(--mobile-browser-toolbar-b, 96px) + env(safe-area-inset-bottom, 0px))",
        // 4px + Safe-Area oben: falls die Drawer-Paper (Notch-bedingt) höher
        // startet als beabsichtigt, bleibt der erste Eintrag trotzdem unter der Uhrzeit/Notch
        pt: "calc(4px + env(safe-area-inset-top, 0px))",
      }}
    >
      <Menu>
        <Menu.Item to="/profile" primaryText="Profil" leftIcon={<UserAvatarIcon size={28} />} />

        {can(permissions, "clubs", "show") && (
          <>
            {open ? (
              <Divider textAlign="left" sx={{ my: 1 }}>
                Admin
              </Divider>
            ) : (
              <Divider sx={{ my: 1 }} />
            )}
            <Menu.Item to="/clubs" primaryText="Vereine" leftIcon={<BusinessIcon />} />
            <Menu.Item to="/challenges" primaryText="Club Challenges" leftIcon={<DashboardIcon />} />
            <Menu.Item to="/challenge-templates" primaryText="Challenge Templates" leftIcon={<DashboardIcon />} />
            <Menu.Item to="/calendar-feeds" primaryText="Kalender-Feeds" leftIcon={<CalendarMonthIcon />} />
          </>
        )}

        {showClubManagement && (
          <>
            {open ? (
              <Divider textAlign="left" sx={{ my: 1 }}>
                Club Management
              </Divider>
            ) : (
              <Divider sx={{ my: 1 }} />
            )}
            {can(permissions, "members", "list") && (
              <Menu.Item to="/users" primaryText="Spieler" leftIcon={<DirectionsRunIcon />} />
            )}
            <Menu.Item to="/teams" primaryText="Teams" leftIcon={<InterpreterModeIcon />} />
            {can(permissions, "jobs", "catalog") && (
              <MenuItemLink to="/events/job-types" primaryText="Job-Katalog" leftIcon={<WorkIcon />} />
            )}
          </>
        )}

        {can(permissions, "events", "list") && (
          <>
            {open ? (
              <Divider textAlign="left" sx={{ my: 1 }}>
                Termine
              </Divider>
            ) : (
              <Divider sx={{ my: 1 }} />
            )}
            {can(permissions, "events", "create") && (
              <Menu.Item to="/events/series" primaryText="Termin erstellen" leftIcon={<EventNoteIcon />} />
            )}
            <Menu.Item to="/events/occurrences" primaryText="Termine" leftIcon={<CalendarMonthIcon />} />
            <Menu.Item to="/calendar" primaryText="Kalender" leftIcon={<CalendarMonthIcon />} />
          </>
        )}

        {open ? <Divider textAlign="left" sx={{ my: 1 }}>Rangliste</Divider> : <Divider sx={{ my: 1 }} />}
        <Menu.Item to="/" primaryText="Punkte" leftIcon={<MilitaryTechIcon />} />
        <MenuItemLink to="/club/leaderboard" primaryText="Rangliste & Challenges" leftIcon={<StarsIcon />} />

        {open ? <Divider textAlign="left" sx={{ my: 1 }}>Training</Divider> : <Divider sx={{ my: 1 }} />}
        {can(permissions, "training", "list") && <Menu.Item to="/play/training" primaryText="Training" leftIcon={<SportsEsportsIcon />} />}
        {can(permissions, "training", "create") && (
          <Menu.Item to="/training-templates" primaryText="Trainingseditor" leftIcon={<ConstructionIcon />} />
        )}
        {can(permissions, "training", "stats") && <Menu.Item to="/coach/training" primaryText="Statistik" leftIcon={<QueryStatsIcon />} />}
        {can(permissions, "training", "dashboard") && (
          <Menu.Item to="/club-dashboard" primaryText="Club Dashboard" leftIcon={<DashboardIcon />} />
        )}
        {can(permissions, "training", "stats") && (
          <Menu.Item to="/personal-dashboard" primaryText="Dashboard" leftIcon={<DashboardIcon />} />
        )}

        {open ? <Divider textAlign="left" sx={{ my: 1 }}>Shop</Divider> : <Divider sx={{ my: 1 }} />}
        <MenuItemLink to="/shop" primaryText="Shop" leftIcon={<StorefrontIcon />} />
        <MenuItemLink to="/shop/my-orders" primaryText="Meine Bestellungen" leftIcon={<ShoppingBagIcon />} />
        {can(permissions, "shop", "manage") && (
          <>
            <MenuItemLink to="/shop-items" primaryText="Artikel verwalten" leftIcon={<StorefrontIcon />} />
            <MenuItemLink to="/shop-orders" primaryText="Bestellungen" leftIcon={<ListAltIcon />} />
            <MenuItemLink to="/shop-settings" primaryText="Shop-Einstellungen" leftIcon={<SettingsIcon />} />
          </>
        )}

        {open ? <Divider textAlign="left" sx={{ my: 1 }}>Strafen</Divider> : <Divider sx={{ my: 1 }} />}
        {can(permissions, "penalties", "manage") && (
          <>
            {can(permissions, "penalties", "catalog") && (
              <MenuItemLink to="/penalty-types" primaryText="Katalog verwalten" leftIcon={<MenuBookIcon />} />
            )}
            <MenuItemLink to="/penalties" primaryText="Strafen vergeben" leftIcon={<GavelIcon />} />
          </>
        )}
        <MenuItemLink to="/penalties/catalog" primaryText="Strafenkatalog" leftIcon={<MenuBookIcon />} />
        <MenuItemLink to="/penalties/my" primaryText="Meine Strafen" leftIcon={<GavelIcon />} />

        {open ? <Divider textAlign="left" sx={{ my: 1 }}>Nachrichten</Divider> : <Divider sx={{ my: 1 }} />}
        {can(permissions, "message", "broadcast") && (
          <MenuItemLink to="/admin/broadcast" primaryText="Nachricht senden" leftIcon={<CampaignIcon />} />
        )}
        {can(permissions, "message", "read") && <InboxMenuItem />}
        {can(permissions, "notifications", "manage") && (
          <MenuItemLink to="/club/notifications" primaryText="Benachrichtigungen" leftIcon={<NotificationsActiveIcon />} />
        )}
      </Menu>
    </Box>
  );
};
